import { useState } from "react";
import { ExternalLink, AlertTriangle, Check, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { EvidenceBadge } from "../Badges";
import { api, errMsg, fmtDate } from "../../lib/api";

const host = (u) => {
  try {
    return new URL(u).hostname.replace(/^www\./, "");
  } catch {
    return u;
  }
};

const ConfirmDialog = ({ f, mode, onClose, onDone }) => {
  const [value, setValue] = useState(f.value || "");
  const [note, setNote] = useState("");
  const [byRestaurant, setByRestaurant] = useState(false);
  const submit = async () => {
    try {
      const { data } = await api.patch(`/findings/${f.id}`, { action: mode, value, note, confirmed_by_restaurant: byRestaurant });
      toast.success("Finding updated — it will be preserved when research is repeated");
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="finding-confirm-dialog">
        <DialogHeader><DialogTitle>{mode === "correct" ? "Correct finding" : "Confirm finding"}: {f.item}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          {mode === "correct" && <Input value={value} onChange={(e) => setValue(e.target.value)} data-testid="finding-correct-value-input" />}
          <Input placeholder="Note (how was this confirmed?)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="finding-confirm-note-input" />
          <label className="flex items-center gap-2"><Checkbox checked={byRestaurant} onCheckedChange={setByRestaurant} data-testid="finding-confirmed-by-restaurant-checkbox" />Confirmed directly by the restaurant</label>
        </div>
        <DialogFooter><Button onClick={submit} className="bg-orange-600 hover:bg-orange-700" data-testid="finding-confirm-submit-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Conflict = ({ f, onDone }) => {
  const resolve = async (accept_new) => {
    try {
      const { data } = await api.post(`/findings/${f.id}/resolve-conflict`, { accept_new });
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mt-2 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-xs" data-testid={`finding-conflict-${f.id}`}>
      <div className="flex items-center gap-1 font-semibold text-yellow-900"><AlertTriangle className="h-3.5 w-3.5" />New research conflicts with the confirmed value</div>
      <div className="mt-1 text-yellow-900">New: <b>{f.conflict.value}</b> ({f.conflict.status}) {f.conflict.source_url && <a className="underline" href={f.conflict.source_url} target="_blank" rel="noreferrer">source</a>}</div>
      <div className="mt-2 flex gap-2">
        <Button size="sm" variant="outline" onClick={() => resolve(false)} data-testid="conflict-keep-btn">Keep confirmed</Button>
        <Button size="sm" variant="outline" onClick={() => resolve(true)} data-testid="conflict-accept-btn">Accept new</Button>
      </div>
    </div>
  );
};

export const FindingRow = ({ f, editable, onChanged }) => {
  const [mode, setMode] = useState(null);
  const tid = f.key?.replace(/[^a-z0-9]+/g, "-");
  return (
    <div className="border-t py-3 first:border-t-0" data-testid={`finding-${tid}`}>
      <div className="grid gap-2 md:grid-cols-[200px,1fr,auto]">
        <div className="text-sm font-semibold text-slate-800">{f.item}</div>
        <div className="min-w-0">
          <div className="break-words text-sm text-slate-900" data-testid={`finding-value-${tid}`}>{f.value}</div>
          {f.excerpt && <div className="mt-1 border-l-2 border-slate-200 pl-2 text-xs italic text-slate-500">“{f.excerpt}”</div>}
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
            {f.source_url && <a href={f.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 font-medium text-slate-700 hover:text-orange-700 hover:underline"><ExternalLink className="h-3 w-3" />{host(f.source_url)}</a>}
            <span>Retrieved {fmtDate(f.retrieved_at)}</span>
            {f.source_date && <span>Published {String(f.source_date).slice(0, 10)}</span>}
            <span>Confidence: {f.confidence}{f.confidence_note ? ` — ${f.confidence_note}` : ""}</span>
            {f.origin === "ai" && <span className="text-slate-400">AI-extracted from cited source</span>}
          </div>
          {f.limitations && <div className="mt-0.5 text-[11px] text-slate-500">Limitations: {f.limitations}</div>}
          {editable && f.confirm_question && <div className="mt-0.5 text-[11px] text-orange-700">Ask: {f.confirm_question}</div>}
          {f.human_confirmed && <div className="mt-1 text-[11px] font-semibold text-blue-700">Confirmed by {f.confirmed_by}{f.human_note ? ` — ${f.human_note}` : ""}{f.original_value ? ` (originally: ${f.original_value})` : ""}</div>}
          {editable && f.conflict && <Conflict f={f} onDone={onChanged} />}
        </div>
        <div className="flex flex-wrap items-start gap-2 md:flex-col md:items-end">
          <EvidenceBadge status={f.status} testId={`finding-status-${tid}`} />
          {editable && (
            <div className="no-print flex gap-1">
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setMode("confirm")} data-testid={`finding-confirm-btn-${tid}`}><Check className="mr-1 h-3 w-3" />Confirm</Button>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setMode("correct")} data-testid={`finding-correct-btn-${tid}`}><Pencil className="mr-1 h-3 w-3" />Correct</Button>
            </div>
          )}
        </div>
      </div>
      {mode && <ConfirmDialog f={f} mode={mode} onClose={() => setMode(null)} onDone={(d) => { setMode(null); onChanged(d); }} />}
    </div>
  );
};
